import { supabase } from './supabaseClient';

/**
 * Fetch all rows from a Supabase query, paginating in batches to avoid the
 * default 1000-row limit.  Pass a query builder **without** `.range()`.
 */
const fetchAllRows = async (buildQuery, pageSize = 1000) => {
  const rows = [];
  let from = 0;
  while (true) {
    const { data, error } = await buildQuery().range(from, from + pageSize - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    rows.push(...data);
    if (data.length < pageSize) break;
    from += pageSize;
  }
  return rows;
};

/**
 * Fetch users with their OneSignal player IDs using direct SQL
 */
export const fetchUsersWithDevices = async (searchQuery = '', roleFilter = 'all', limit = 50, offset = 0) => {
  try {
    // First get users
    let userQuery = supabase
      .from('users')
      .select(`
        uuid,
        email,
        first_name,
        last_name,
        nickname,
        handle,
        profile_img,
        preferences,
        created_at,
        updated_at,
        agreed_to_terms,
        test_account
      `)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    // Add search filter if provided
    if (searchQuery && searchQuery.trim()) {
      userQuery = userQuery.or(`
        first_name.ilike.%${searchQuery}%,
        last_name.ilike.%${searchQuery}%,
        email.ilike.%${searchQuery}%,
        handle.ilike.%${searchQuery}%,
        nickname.ilike.%${searchQuery}%
      `);
    }

    // Note: Role filtering will be done in JavaScript after fetching users
    // This allows for more flexible matching of role values

    const { data: users, error: userError } = await userQuery;
    if (userError) {
      console.error('Error fetching users:', userError);
      throw userError;
    }

    // Apply role filtering in JavaScript for more flexible matching
    let filteredUsers = users || [];
    if (roleFilter !== 'all') {
      filteredUsers = filteredUsers.filter(user => {
        const userRole = user.preferences?.home;
        
        if (roleFilter === 'unknown') {
          return !userRole || userRole === '' || userRole === 'unknown' || userRole === 'null' || userRole === 'undefined';
        }
        
        // Normalize both the filter and the user role for comparison
        const normalizedFilter = roleFilter.toLowerCase().trim();
        const normalizedUserRole = (userRole || '').toLowerCase().trim();
        
        // Handle common variations
        if (normalizedFilter === 'collector' && (normalizedUserRole === 'collector' || normalizedUserRole === 'collectors')) return true;
        if (normalizedFilter === 'artist' && (normalizedUserRole === 'artist' || normalizedUserRole === 'artists')) return true;
        if (normalizedFilter === 'gallery' && (normalizedUserRole === 'gallery' || normalizedUserRole === 'galleries')) return true;
        if (normalizedFilter === 'viewer' && (normalizedUserRole === 'viewer' || normalizedUserRole === 'viewers')) return true;
        
        return normalizedUserRole === normalizedFilter;
      });
    }

    // Handle case where no users are found
    if (!filteredUsers || filteredUsers.length === 0) {
      return {
        users: [],
        total: 0,
        hasMore: false
      };
    }

    // Then get device counts and device info for each user
    const userIds = filteredUsers.map(u => u.uuid);
    const { data: deviceRecords, error: deviceError } = await supabase
      .from('user_player_ids')
      .select('user_id, device_info, player_id, created_at, updated_at')
      .in('user_id', userIds);

    if (deviceError) {
      console.warn('Error fetching device records:', deviceError);
      // Continue without device info
    }

    // Combine the data
    const usersWithDevices = filteredUsers.map(user => {
      // Normalize role values from database
      let role = user.preferences?.home || 'unknown';
      if (role && typeof role === 'string') {
        role = role.toLowerCase().trim();
        if (role === 'collectors') role = 'collector';
        if (role === 'artists') role = 'artist';
        if (role === 'galleries') role = 'gallery';
        if (role === 'viewers') role = 'viewer';
        if (role === '' || role === 'null' || role === 'undefined') role = 'unknown';
      }
      // Attach all device records for this user
      const userDevices = deviceRecords ? deviceRecords.filter(d => d.user_id === user.uuid) : [];
      return {
        ...user,
        role: role,
        device_count: userDevices.length,
        notification_enabled: user.preferences?.notifications !== false,
        user_player_ids: userDevices,
        display_name: user.nickname || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
        initials: getInitials(user.first_name, user.last_name, user.nickname)
      };
    });

    // Debug: Log role distribution to help identify issues
    if (usersWithDevices.length > 0) {
      const roleCounts = {};
      usersWithDevices.forEach(user => {
        roleCounts[user.role] = (roleCounts[user.role] || 0) + 1;
      });
      console.log('Role distribution in fetched users:', roleCounts);
    }

    return {
      users: usersWithDevices,
      total: filteredUsers.length,
      hasMore: filteredUsers.length === limit
    };

  } catch (error) {
    console.error('Error fetching users with devices:', error);
    // Return empty result instead of throwing
    return {
      users: [],
      total: 0,
      hasMore: false,
      error: error.message
    };
  }
};

/**
 * Helper function to get user initials
 */
function getInitials(firstName, lastName, nickname) {
  if (nickname) return nickname.charAt(0).toUpperCase();
  
  const first = firstName ? firstName.charAt(0).toUpperCase() : '';
  const last = lastName ? lastName.charAt(0).toUpperCase() : '';
  
  return (first + last) || '?';
}

/**
 * Get user statistics for dashboard using direct SQL
 */
export const getUserStats = async () => {
  try {
    // Get total user count (excluding test accounts)
    const { count: totalUsers, error: totalError } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true })
      .neq('test_account', true);

    if (totalError) {
      console.warn('Error fetching total users:', totalError);
    }

    // Get active users (last 7 days)
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    const { count: activeUsers, error: activeError } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true })
      .neq('test_account', true)
      .gte('updated_at', weekAgo);

    if (activeError) {
      console.warn('Error fetching active users:', activeError);
    }

    // Get unique users with notifications enabled (have player IDs)
    const { data: notificationData, error: notifError } = await supabase
      .from('user_player_ids')
      .select('user_id');

    let notificationUsers = 0;
    if (!notifError && notificationData) {
      // Count unique user IDs
      const uniqueUserIds = new Set(notificationData.map(item => item.user_id));
      notificationUsers = uniqueUserIds.size;
    }

    // Get total device count
    const { count: totalDevices, error: deviceError } = await supabase
      .from('user_player_ids')
      .select('*', { count: 'exact', head: true });

    if (deviceError) {
      console.warn('Error fetching device count:', deviceError);
    }

    // NEW: Get mobile app users
    const mobileAppData = await getUsersWithMobileApp();

    return {
      totalUsers: totalUsers || 0,
      activeUsers: activeUsers || 0,
      notificationEnabled: notificationUsers || 0,
      totalDevices: totalDevices || 0,
      // NEW: Mobile app specific metrics
      mobileAppUsers: mobileAppData.mobileUsers || 0,
      mobileAppDevices: mobileAppData.mobileDevices || 0
    };

  } catch (error) {
    console.error('Error fetching user stats:', error);
    return {
      totalUsers: 0,
      activeUsers: 0,
      notificationEnabled: 0,
      totalDevices: 0,
      mobileAppUsers: 0,
      mobileAppDevices: 0
    };
  }
};

/**
 * Send system notification using enhanced OneSignal v2 edge function
 */
export const sendSystemNotification = async (userUuids, title, message, category = 'system_announcement', priority = 1, adminUser = null) => {
  try {
    if (!userUuids || userUuids.length === 0) {
      throw new Error('No users selected for notification');
    }

    const results = [];
    
    // Send notifications to each user individually using existing enhanced function
    for (const userUuid of userUuids) {
      try {
        const { data, error } = await supabase.functions.invoke('onesignal_push_artist_v2', {
          body: {
            externalId: userUuid,
            senderId: adminUser?.uuid || null,
            senderType: 'system', // Must be 'user' or 'system' per edge function validation
            title,
            message,
            category,
            priority,
            metadata: {
              admin_broadcast: true,
              admin_user_id: adminUser?.uuid,
              admin_user_email: adminUser?.email,
              broadcast_timestamp: new Date().toISOString(),
              recipient_count: userUuids.length,
              broadcast_id: `admin_${Date.now()}`, // Unique broadcast ID
              sent_by_admin: true // Flag to indicate this was sent by admin
            }
          }
        });

        if (error) {
          console.error(`Failed to send notification to ${userUuid}:`, error);
          results.push({ userUuid, success: false, error: error.message });
        } else {
          results.push({ userUuid, success: true, data });
        }
      } catch (individualError) {
        console.error(`Error sending to ${userUuid}:`, individualError);
        results.push({ userUuid, success: false, error: individualError.message });
      }
    }

    const successCount = results.filter(r => r.success).length;
    const failureCount = results.filter(r => !r.success).length;

    // Log admin broadcast for audit trail
    try {
      await supabase.from('admin_actions').insert({
        admin_user_id: adminUser?.uuid,
        admin_type: 'company_admin',
        action_type: 'broadcast_notification',
        target_count: userUuids.length,
        successful_count: successCount,
        failed_count: failureCount,
        metadata: {
          title,
          message,
          category,
          priority,
          timestamp: new Date().toISOString()
        }
      });
    } catch (auditError) {
      console.warn('Failed to log admin action:', auditError);
    }

    return {
      success: successCount > 0,
      total_sent: userUuids.length,
      successful: successCount,
      failed: failureCount,
      results
    };

  } catch (error) {
    console.error('Error sending system notifications:', error);
    return {
      success: false,
      total_sent: userUuids?.length || 0,
      successful: 0,
      failed: userUuids?.length || 0,
      error: error.message
    };
  }
};

/**
 * Format last active time for display
 */
export const formatLastActive = (dateString) => {
  if (!dateString) return 'Never';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor((now - date) / (1000 * 60));
  
  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `${diffInDays}d ago`;
  
  return date.toLocaleDateString();
};

/**
 * Get notification categories with counts (for dashboard overview)
 */
export const getNotificationCategories = async () => {
  try {
    const { data: categories, error } = await supabase
      .from('notification_categories')
      .select('*')
      .eq('is_active', true)
      .order('section, sort_order');

    if (error) throw error;
    return categories || [];
  } catch (error) {
    console.error('Error fetching notification categories:', error);
    return [];
  }
};

/**
 * Get notification sections (for dashboard overview)
 */
export const getNotificationSections = async () => {
  try {
    const { data: sections, error } = await supabase
      .from('notification_sections')
      .select('*')
      .eq('is_active', true)
      .order('sort_order');

    if (error) throw error;
    return sections || [];
  } catch (error) {
    console.error('Error fetching notification sections:', error);
    return [];
  }
};

/**
 * Get notification statistics for dashboard
 */
export const getNotificationStats = async () => {
  try {
    // Get total notifications in last 30 days
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    
    const { count: totalNotifications, error: totalError } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .gte('created_at', thirtyDaysAgo);

    if (totalError) console.warn('Error getting total notifications:', totalError);

    // Get active categories count
    const { count: activeCategories, error: categoriesError } = await supabase
      .from('notification_categories')
      .select('*', { count: 'exact', head: true })
      .eq('is_active', true);

    if (categoriesError) console.warn('Error getting categories count:', categoriesError);

    // Get notifications sent (with notification_sent = true)
    const { count: notificationsSent, error: sentError } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('notification_sent', true)
      .gte('created_at', thirtyDaysAgo);

    if (sentError) console.warn('Error getting sent notifications:', sentError);

    // Get read rate (rough calculation)
    const { count: readNotifications, error: readError } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('read', true)
      .gte('created_at', thirtyDaysAgo);

    if (readError) console.warn('Error getting read notifications:', readError);

    const readRate = totalNotifications > 0 ? 
      Math.round((readNotifications / totalNotifications) * 100) : 0;

    return {
      total_notifications: totalNotifications || 0,
      active_categories: activeCategories || 0,
      notifications_sent: notificationsSent || 0,
      read_rate: readRate,
      performance: notificationsSent && totalNotifications ? 
        Math.round((notificationsSent / totalNotifications) * 100) : 0
    };
    
  } catch (error) {
    console.error('Error getting notification stats:', error);
    return {
      total_notifications: 0,
      active_categories: 0,
      notifications_sent: 0,
      read_rate: 0,
      performance: 0
    };
  }
};

/**
 * Get recent admin communication activity
 */
export const getRecentCommunicationActivity = async (limit = 10) => {
  try {
    const { data: activities, error } = await supabase
      .from('admin_actions')
      .select(`
        *,
        admin:users!admin_actions_admin_user_id_fkey(nickname, first_name, last_name)
      `)
      .like('action_type', '%communication%')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    // Format the data for display
    const formattedActivities = (activities || []).map(activity => ({
      id: activity.id,
      action_type: activity.action_type,
      admin_name: activity.admin?.nickname || 
                  `${activity.admin?.first_name || ''} ${activity.admin?.last_name || ''}`.trim() ||
                  'System',
      target_count: activity.target_count || 0,
      successful_count: activity.successful_count || 0,
      collection_title: activity.metadata?.collection_title || 'Unknown Collection',
      communication_type: activity.metadata?.communication_type || 'general',
      created_at: activity.created_at,
      // Calculate time ago
      time_ago: getTimeAgo(activity.created_at)
    }));

    return formattedActivities;
    
  } catch (error) {
    console.error('Error getting recent communication activity:', error);
    return [];
  }
};

/**
 * Get notification analytics by category
 */
export const getNotificationAnalyticsByCategory = async () => {
  try {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
    
    const { data: analytics, error } = await supabase
      .from('notifications')
      .select('category, read, notification_sent, created_at')
      .gte('created_at', thirtyDaysAgo)
      .not('category', 'is', null);

    if (error) throw error;

    // Group by category and calculate stats
    const categoryStats = {};
    
    (analytics || []).forEach(notification => {
      const category = notification.category || 'uncategorized';
      
      if (!categoryStats[category]) {
        categoryStats[category] = {
          total: 0,
          sent: 0,
          read: 0,
          category_key: category
        };
      }
      
      categoryStats[category].total++;
      if (notification.notification_sent) categoryStats[category].sent++;
      if (notification.read) categoryStats[category].read++;
    });

    // Convert to array and add percentages
    const result = Object.values(categoryStats).map(stat => ({
      ...stat,
      send_rate: stat.total > 0 ? Math.round((stat.sent / stat.total) * 100) : 0,
      read_rate: stat.sent > 0 ? Math.round((stat.read / stat.sent) * 100) : 0
    }));

    return result.sort((a, b) => b.total - a.total); // Sort by total descending
    
  } catch (error) {
    console.error('Error getting notification analytics by category:', error);
    return [];
  }
};

/**
 * Get user notification preferences summary
 */
export const getNotificationPreferencesSummary = async () => {
  try {
    // Get total users with notification preferences
    const { count: totalWithPrefs, error: prefsError } = await supabase
      .from('notification_preferences')
      .select('*', { count: 'exact', head: true });

    if (prefsError) console.warn('Error getting preferences count:', prefsError);

    // Get users with notifications enabled
    const { count: notificationsEnabled, error: enabledError } = await supabase
      .from('notification_preferences')
      .select('*', { count: 'exact', head: true })
      .eq('device_notifications_enabled', true);

    if (enabledError) console.warn('Error getting enabled count:', enabledError);

    // Get total users for context
    const { count: totalUsers, error: usersError } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true })
      .neq('test_account', true);

    if (usersError) console.warn('Error getting total users:', usersError);

    return {
      total_users: totalUsers || 0,
      users_with_preferences: totalWithPrefs || 0,
      notifications_enabled: notificationsEnabled || 0,
      preferences_rate: totalUsers > 0 ? 
        Math.round((totalWithPrefs / totalUsers) * 100) : 0,
      enabled_rate: totalWithPrefs > 0 ? 
        Math.round((notificationsEnabled / totalWithPrefs) * 100) : 0
    };
    
  } catch (error) {
    console.error('Error getting notification preferences summary:', error);
    return {
      total_users: 0,
      users_with_preferences: 0,
      notifications_enabled: 0,
      preferences_rate: 0,
      enabled_rate: 0
    };
  }
};

/**
 * Test notification functionality (for admin testing)
 */
export const sendTestNotification = async (categoryKey, adminUserId) => {
  try {
    const testRecipients = ["elva@vibrantcast.com", "steve@vibrantcast.com"];
    
    // Get category details
    const { data: category, error: categoryError } = await supabase
      .from('notification_categories')
      .select('title, description')
      .eq('category_key', categoryKey)
      .single();

    if (categoryError) throw categoryError;

    // Send to test admin accounts
    for (const email of testRecipients) {
      // Find admin user UUID
      const { data: user } = await supabase
        .from('users')
        .select('uuid')
        .eq('email', email)
        .single();

      if (user) {
        await supabase.from('notifications').insert({
          user_id: user.uuid,
          title: `Test: ${category.title}`,
          body: `This is a test notification for category "${categoryKey}". ${category.description}`,
          type: 'test_notification',
          category: categoryKey,
          priority: 1,
          sender_id: adminUserId,
          sender_type: 'admin',
          metadata: {
            test_mode: true,
            sent_by: adminUserId,
            timestamp: new Date().toISOString()
          }
        });
      }
    }

    // Log the test action
    await supabase.from('admin_actions').insert({
      admin_user_id: adminUserId,
      admin_type: 'company_admin',
      action_type: 'test_notification',
      target_count: testRecipients.length,
      successful_count: testRecipients.length,
      metadata: {
        category_key: categoryKey,
        category_title: category.title,
        test_recipients: testRecipients,
        timestamp: new Date().toISOString()
      }
    });

    return {
      success: true,
      message: `Test notification sent to ${testRecipients.length} admin accounts`,
      category: category.title
    };
    
  } catch (error) {
    console.error('Error sending test notification:', error);
    return {
      success: false,
      message: `Failed to send test notification: ${error.message}`
    };
  }
};

/**
 * Helper function to calculate time ago
 */
function getTimeAgo(timestamp) {
  const now = new Date();
  const time = new Date(timestamp);
  const diffMs = now - time;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays < 7) return `${diffDays} days ago`;
  return time.toLocaleDateString();
};

/**
 * Delete/deactivate a notification category (admin only)
 */
export const deactivateNotificationCategory = async (categoryId, adminUserId) => {
  try {
    const { data, error } = await supabase
      .from('notification_categories')
      .update({ 
        is_active: false,
        updated_at: new Date().toISOString()
      })
      .eq('id', categoryId)
      .select()
      .single();

    if (error) throw error;

    // Log the action
    await supabase.from('admin_actions').insert({
      admin_user_id: adminUserId,
      admin_type: 'company_admin',
      action_type: 'deactivate_notification_category',
      metadata: {
        category_id: categoryId,
        category_key: data.category_key,
        category_title: data.title,
        timestamp: new Date().toISOString()
      }
    });

    return {
      success: true,
      message: `Category "${data.title}" has been deactivated`
    };
    
  } catch (error) {
    console.error('Error deactivating notification category:', error);
    return {
      success: false,
      message: `Failed to deactivate category: ${error.message}`
    };
  }
};

// Replace the first sendSystemNotification function with this renamed version:

/**
 * Send notification to specific users by UUID (for UserDashboard component)
 */
export const sendNotificationToSpecificUsers = async (userUuids, title, message, category = 'system_announcement', priority = 1, adminUser = null) => {
  try {
    if (!userUuids || userUuids.length === 0) {
      throw new Error('No users selected for notification');
    }

    const results = [];
    
    // Send notifications to each user individually using existing enhanced function
    for (const userUuid of userUuids) {
      try {
        const { data, error } = await supabase.functions.invoke('onesignal_push_artist_v2', {
          body: {
            externalId: userUuid,
            senderId: adminUser?.uuid || null,
            senderType: 'admin',
            title,
            message,
            category,
            priority,
            metadata: {
              admin_broadcast: true,
              admin_user_id: adminUser?.uuid,
              admin_user_email: adminUser?.email,
              broadcast_timestamp: new Date().toISOString(),
              recipient_count: userUuids.length,
              broadcast_id: `admin_${Date.now()}` // Unique broadcast ID
            }
          }
        });

        if (error) {
          console.error(`Failed to send notification to ${userUuid}:`, error);
          results.push({ userUuid, success: false, error: error.message });
        } else {
          results.push({ userUuid, success: true, data });
        }
      } catch (individualError) {
        console.error(`Error sending to ${userUuid}:`, individualError);
        results.push({ userUuid, success: false, error: individualError.message });
      }
    }

    const successCount = results.filter(r => r.success).length;
    const failureCount = results.filter(r => !r.success).length;

    // Log admin broadcast for audit trail
    try {
      await supabase.from('admin_actions').insert({
        admin_user_id: adminUser?.uuid,
        admin_type: 'company_admin',
        action_type: 'broadcast_notification',
        target_count: userUuids.length,
        successful_count: successCount,
        failed_count: failureCount,
        metadata: {
          title,
          message,
          category,
          priority,
          timestamp: new Date().toISOString()
        }
      });
    } catch (auditError) {
      console.warn('Failed to log admin action:', auditError);
    }

    return {
      success: successCount > 0,
      total_sent: userUuids.length,
      successful: successCount,
      failed: failureCount,
      results
    };

  } catch (error) {
    console.error('Error sending system notifications:', error);
    return {
      success: false,
      total_sent: userUuids?.length || 0,
      successful: 0,
      failed: userUuids?.length || 0,
      error: error.message
    };
  }
};

/**
 * Send notification to specific users by UUID
 */
export const sendNotificationToUsers = async (userIds, notificationData) => {
  try {
    const {
      title,
      message,
      category_key,
      priority = 1,
      admin_user_id,
      action_url,
      metadata = {}
    } = notificationData;

    // Create notifications for each user
    const notifications = userIds.map(user_id => ({
      user_id,
      title,
      body: message,
      type: 'admin_notification',
      category: category_key,
      priority,
      sender_id: admin_user_id,
      sender_type: 'admin',
      action_url,
      metadata: {
        ...metadata,
        sent_by_admin: true,
        sent_at: new Date().toISOString()
      }
    }));

    const { data, error } = await supabase
      .from('notifications')
      .insert(notifications)
      .select();

    if (error) throw error;

    // Also send push notifications if users have the app
    for (const user_id of userIds) {
      try {
        await fetch('https://api.vibrantcast.com/functions/v1/onesignal_push_artist_v2', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            externalId: user_id,
            title,
            message,
            category: category_key,
            metadata: { admin_sent: true, action_url }
          })
        });
      } catch (pushError) {
        console.warn(`Push notification failed for user ${user_id}:`, pushError);
      }
    }

    // Log the admin action
    await supabase.from('admin_actions').insert({
      admin_user_id,
      admin_type: 'company_admin',
      action_type: 'send_notification_to_users',
      target_count: userIds.length,
      successful_count: data?.length || 0,
      metadata: {
        category_key,
        title,
        target_user_ids: userIds,
        timestamp: new Date().toISOString()
      }
    });

    return {
      success: true,
      notifications_sent: data?.length || 0,
      message: `Notification sent to ${userIds.length} users`
    };
  } catch (error) {
    console.error('Error sending notification to users:', error);
    throw error;
  }
};

/**
 * Get users for notification targeting with app status
 */
export const getUsersForNotificationTargeting = async (filters = {}) => {
  try {
    const {
      user_types = [],
      has_app_only = false,
      search_term = '',
      include_test_accounts = false,
      limit = 2000,
      offset = 0
    } = filters;

    console.log('Fetching users with filters:', { user_types, has_app_only, search_term, include_test_accounts, limit, offset });

    // First, get ALL app users to mark them
    const { data: appUsers, error: appError } = await supabase
      .from('user_player_ids')
      .select('user_id');
    
    if (appError) {
      console.error('Error fetching app users:', appError);
    }
    const appUserIds = new Set(appUsers?.map(u => u.user_id) || []);
    console.log(`Found ${appUserIds.size} users with the app installed`);

    // Build base query
    let query = supabase
      .from('users')
      .select(`
        uuid,
        email,
        first_name,
        last_name,
        nickname,
        preferences,
        test_account
      `, { count: 'exact' })
      .order('nickname', { ascending: true, nullsFirst: false });

    // Filter out test accounts unless explicitly included
    if (!include_test_accounts) {
      query = query.or('test_account.is.null,test_account.eq.false');
    }

    // Filter by search term
    if (search_term) {
      query = query.or(`email.ilike.%${search_term}%,nickname.ilike.%${search_term}%,first_name.ilike.%${search_term}%`);
    }

    // If has_app_only, filter to only users in user_player_ids
    if (has_app_only && appUserIds.size > 0) {
      // Use IN filter for app users
      query = query.in('uuid', Array.from(appUserIds));
    }

    // Apply pagination
    query = query.range(offset, offset + limit - 1);

    const { data: users, error, count } = await query;
    if (error) {
      console.error('Error fetching users:', error);
      throw error;
    }

    console.log(`Fetched ${users?.length || 0} users from database (total: ${count})`);

    // Filter by user types in JavaScript (JSONB filters are tricky in PostgREST)
    let filteredUsers = users || [];
    if (user_types.length > 0) {
      filteredUsers = filteredUsers.filter(user => {
        const userHome = user.preferences?.home?.toLowerCase();
        return user_types.some(type => type.toLowerCase() === userHome);
      });
      console.log(`After type filter: ${filteredUsers.length} users`);
    }

    // Mark each user with hasApp status
    filteredUsers = filteredUsers.map(user => ({
      ...user,
      hasApp: appUserIds.has(user.uuid)
    }));

    console.log(`Returning ${filteredUsers.length} target users`);
    return {
      users: filteredUsers,
      total: filteredUsers.length,
      hasMore: false
    };
  } catch (error) {
    console.error('Error getting users for targeting:', error);
    return { users: [], total: 0, hasMore: false };
  }
};

/**
 * Get upcoming events for notification targeting (lightweight query)
 */
export const getEventsForTargeting = async () => {
  try {
    const { data, error } = await supabase
      .from('events')
      .select('id, name, event_start, eventbrite_id')
      .order('event_start', { ascending: false })
      .limit(50);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching events for targeting:', error);
    return [];
  }
};

/**
 * Get users who have NOT purchased tickets for a specific event.
 * Checks both Stripe (user_event_tickets) and Eventbrite (rsvp_attendees) sources.
 */
export const getNonTicketPurchasersForEvent = async (eventId, eventbriteId, options = {}) => {
  try {
    const { include_test_accounts = false, has_app_only = false } = options;

    // 1. Get Stripe ticket purchasers for this event
    const { data: stripeTickets, error: stripeError } = await supabase
      .from('user_event_tickets')
      .select('user_uuid')
      .eq('event_id', eventId);

    if (stripeError) console.error('Error fetching Stripe tickets:', stripeError);

    const purchaserUuids = new Set(
      (stripeTickets || []).map(t => t.user_uuid).filter(Boolean)
    );

    // 2. Get Eventbrite attendees for this event (if it has an Eventbrite ID)
    if (eventbriteId) {
      const { data: ebAttendees, error: ebError } = await supabase
        .from('rsvp_attendees')
        .select('user_uuid')
        .eq('event_id', eventbriteId);

      if (ebError) console.error('Error fetching Eventbrite attendees:', ebError);

      (ebAttendees || []).forEach(a => {
        if (a.user_uuid) purchaserUuids.add(a.user_uuid);
      });
    }

    console.log(`Found ${purchaserUuids.size} ticket purchasers for event ${eventId}`);

    // 3. Get all app users (for hasApp marking)
    const { data: appUsers } = await supabase
      .from('user_player_ids')
      .select('user_id');
    const appUserIds = new Set(appUsers?.map(u => u.user_id) || []);

    // 4. Get all users
    let query = supabase
      .from('users')
      .select('uuid, email, first_name, last_name, nickname, preferences, test_account', { count: 'exact' })
      .order('nickname', { ascending: true, nullsFirst: false });

    if (!include_test_accounts) {
      query = query.or('test_account.is.null,test_account.eq.false');
    }

    const { data: allUsers, error: usersError } = await query;
    if (usersError) throw usersError;

    // 5. Filter out users who already purchased tickets
    let nonPurchasers = (allUsers || [])
      .filter(u => !purchaserUuids.has(u.uuid))
      .map(u => ({
        ...u,
        hasApp: appUserIds.has(u.uuid)
      }));

    // 6. Optionally filter to only users with the app installed
    if (has_app_only) {
      nonPurchasers = nonPurchasers.filter(u => u.hasApp);
    }

    console.log(`${nonPurchasers.length} users have NOT purchased tickets for event ${eventId}`);

    return {
      users: nonPurchasers,
      total: nonPurchasers.length,
      purchaserCount: purchaserUuids.size
    };
  } catch (error) {
    console.error('Error getting non-ticket-purchasers:', error);
    return { users: [], total: 0, purchaserCount: 0 };
  }
};

// Additional functions to add to your existing adminApi.js

/**
 * Fetch detailed user information by ID
 */
export const fetchUserDetails = async (userId) => {
  try {
    const { data: user, error } = await supabase
      .from('users')
      .select(`
        uuid,
        email,
        first_name,
        last_name,
        nickname,
        handle,
        profile_img,
        preferences,
        created_at,
        updated_at,
        agreed_to_terms,
        test_account,
        user_player_ids (
          id,
          player_id,
          device_info,
          token,
          created_at,
          updated_at,
          is_active
        )
      `)
      .eq('uuid', userId)
      .single();

    if (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }

    if (!user) {
      throw new Error('User not found');
    }

    // Transform data to match expected format
    return {
      ...user,
      role: user.preferences?.home || 'unknown',
      device_count: user.user_player_ids?.length || 0,
      notification_enabled: user.preferences?.notifications !== false,
      display_name: user.nickname || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email,
      initials: getInitials(user.first_name, user.last_name, user.nickname)
    };

  } catch (error) {
    console.error('Error fetching user details:', error);
    throw error;
  }
};

/**
 * Get user activity feed using existing user_activity table
 */
export const getUserActivity = async (userId, timeRange = '7d') => {
  try {
    // Calculate date range
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

    // Get user activities from existing user_activity table
    const { data: activities, error: activitiesError } = await supabase
      .from('user_activity')
      .select(`
        id,
        activity_type,
        timestamp,
        vc_id,
        collection_id,
        gallery_id,
        submission_id,
        email,
        note,
        artist_id,
        tag_id,
        answers,
        media_id,
        story_id
      `)
      .eq('uuid', userId)
      .gte('timestamp', startDate)
      .eq('hidden', false)
      .order('timestamp', { ascending: false })
      .limit(100);

    if (activitiesError) {
      console.error('Error fetching user activities:', activitiesError);
      return [];
    }

    if (!activities || activities.length === 0) {
      return [];
    }

    // Transform activities to consistent format
    const formattedActivities = activities.map(activity => {
      let description = 'Unknown activity';
      let metadata = '';

      switch (activity.activity_type) {
        case 'login':
        case 'app_open':
          description = 'Opened the app';
          break;
        case 'view_artwork':
        case 'artwork_view':
          description = 'Viewed an artwork';
          metadata = activity.vc_id ? `Artwork ID: ${activity.vc_id}` : '';
          break;
        case 'bookmark':
        case 'bookmark_artwork':
          description = 'Bookmarked an artwork';
          metadata = activity.vc_id ? `Artwork ID: ${activity.vc_id}` : '';
          break;
        case 'follow':
        case 'follow_artist':
          description = 'Followed an artist';
          metadata = activity.artist_id ? `Artist: ${activity.artist_id}` : '';
          break;
        case 'submission':
        case 'artwork_submission':
          description = 'Submitted artwork to collection';
          metadata = activity.collection_id ? `Collection ID: ${activity.collection_id}` : '';
          break;
        case 'gallery_visit':
          description = 'Visited a gallery';
          metadata = activity.gallery_id ? `Gallery ID: ${activity.gallery_id}` : '';
          break;
        case 'story_interaction':
          description = 'Interacted with a story';
          metadata = activity.story_id ? `Story ID: ${activity.story_id}` : '';
          break;
        case 'tag_interaction':
          description = 'Interacted with artwork tags';
          metadata = activity.tag_id ? `Tag ID: ${activity.tag_id}` : '';
          break;
        case 'media_interaction':
          description = 'Interacted with media';
          metadata = activity.media_id ? `Media ID: ${activity.media_id}` : '';
          break;
        default:
          description = `${activity.activity_type.replace('_', ' ')}`;
          break;
      }

      // Add note if available
      if (activity.note) {
        metadata = metadata ? `${metadata} - ${activity.note}` : activity.note;
      }

      // Add answers if available (for submissions)
      if (activity.answers && typeof activity.answers === 'object') {
        metadata = metadata ? `${metadata} - Answers provided` : 'Answers provided';
      }

      return {
        type: activity.activity_type,
        description,
        timestamp: activity.timestamp,
        metadata: metadata || null,
        rawData: activity // Include raw data for debugging
      };
    });

    return formattedActivities;

  } catch (error) {
    console.error('Error fetching user activity:', error);
    return [];
  }
};

/**
 * Get user statistics for detail view using existing tables
 */
export const getUserDetailStats = async (userId) => {
  try {
    // Get total activity count from user_activity table
    const { count: totalActivities } = await supabase
      .from('user_activity')
      .select('*', { count: 'exact', head: true })
      .eq('uuid', userId)
      .eq('hidden', false);

    // Get recent activity count (last 7 days)
    const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
    
    const { count: recentActivities } = await supabase
      .from('user_activity')
      .select('*', { count: 'exact', head: true })
      .eq('uuid', userId)
      .eq('hidden', false)
      .gte('timestamp', weekAgo);

    // Get login/app open count (last 7 days)
    const { count: loginCount } = await supabase
      .from('user_activity')
      .select('*', { count: 'exact', head: true })
      .eq('uuid', userId)
      .eq('hidden', false)
      .in('activity_type', ['login', 'app_open'])
      .gte('timestamp', weekAgo);

    // Get bookmark count
    const { count: totalBookmarks } = await supabase
      .from('user_bookmarks')
      .select('*', { count: 'exact', head: true })
      .eq('uuid', userId);

    const { count: bookmarkCount } = await supabase
      .from('user_activity')
      .select('*', { count: 'exact', head: true })
      .eq('uuid', userId)
      .eq('hidden', false)
      .in('activity_type', ['bookmark', 'bookmark_artwork'])
      .gte('timestamp', weekAgo);

    // Get artwork view count
    const { count: totalViews } = await supabase
      .from('user_activity')
      .select('*', { count: 'exact', head: true })
      .eq('uuid', userId)
      .eq('hidden', false)
      .in('activity_type', ['view_artwork', 'artwork_view']);

    const { count: viewCount } = await supabase
      .from('user_activity')
      .select('*', { count: 'exact', head: true })
      .eq('uuid', userId)
      .eq('hidden', false)
      .in('activity_type', ['view_artwork', 'artwork_view'])
      .gte('timestamp', weekAgo);

    // Get follow/social activity count
    const { count: socialCount } = await supabase
      .from('user_activity')
      .select('*', { count: 'exact', head: true })
      .eq('uuid', userId)
      .eq('hidden', false)
      .in('activity_type', ['follow', 'follow_artist'])
      .gte('timestamp', weekAgo);

    // Estimate total time spent (placeholder - based on activities)
    const totalTimeSpent = (totalActivities || 0) * 2; // Assume 2 minutes average per activity

    return {
      totalSessions: totalActivities || 0,
      loginCount: loginCount || 0,
      totalBookmarks: totalBookmarks || 0,
      bookmarkCount: bookmarkCount || 0,
      totalViews: totalViews || 0,
      viewCount: viewCount || 0,
      likeCount: socialCount || 0, // Using social activity as proxy for likes
      totalTimeSpent: totalTimeSpent || 0,
      recentActivities: recentActivities || 0
    };

  } catch (error) {
    console.error('Error fetching user stats:', error);
    return {
      totalSessions: 0,
      loginCount: 0,
      totalBookmarks: 0,
      bookmarkCount: 0,
      totalViews: 0,
      viewCount: 0,
      likeCount: 0,
      totalTimeSpent: 0,
      recentActivities: 0
    };
  }
};

/**
 * Update user preferences (admin action)
 */
export const updateUserPreferences = async (userId, preferences, adminUser) => {
  try {
    // Log admin action
    await logAdminAction(adminUser, 'update_user_preferences', {
      target_user_id: userId,
      preferences: preferences
    });

    const { data, error } = await supabase
      .from('users')
      .update({ 
        preferences: preferences,
        updated_at: new Date().toISOString()
      })
      .eq('uuid', userId)
      .select()
      .single();

    if (error) {
      console.error('Error updating user preferences:', error);
      throw error;
    }

    return data;

  } catch (error) {
    console.error('Error updating user preferences:', error);
    throw error;
  }
};

/**
 * Toggle user account status (admin action)
 */
export const toggleUserStatus = async (userId, isActive, adminUser) => {
  try {
    // Log admin action
    await logAdminAction(adminUser, 'toggle_user_status', {
      target_user_id: userId,
      new_status: isActive ? 'active' : 'suspended'
    });

    const { data, error } = await supabase
      .from('users')
      .update({ 
        is_active: isActive,
        updated_at: new Date().toISOString()
      })
      .eq('uuid', userId)
      .select()
      .single();

    if (error) {
      console.error('Error toggling user status:', error);
      throw error;
    }

    return data;

  } catch (error) {
    console.error('Error toggling user status:', error);
    throw error;
  }
};

/**
 * Log admin actions for audit trail using existing admin_actions table
 */
const logAdminAction = async (adminUser, actionType, metadata = {}) => {
  try {
    await supabase
      .from('admin_actions')
      .insert({
        admin_user_id: adminUser.uuid,
        admin_type: adminUser.admin_type || 'company_admin',
        action_type: actionType,
        metadata: metadata,
        created_at: new Date().toISOString()
      });
  } catch (error) {
    console.warn('Failed to log admin action:', error);
    // Don't throw - logging failure shouldn't break the main action
  }
};

/**
 * Get user engagement metrics using user_activity data
 */
export const getUserEngagementMetrics = async (userId, timeRange = '30d') => {
  try {
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

    // Get daily engagement data from user_activity
    const { data: activities } = await supabase
      .from('user_activity')
      .select('timestamp, activity_type')
      .eq('uuid', userId)
      .eq('hidden', false)
      .gte('timestamp', startDate)
      .order('timestamp', { ascending: true });

    // Process data for charts
    const dailyMetrics = {};
    
    if (activities) {
      activities.forEach(activity => {
        const date = new Date(activity.timestamp).toISOString().split('T')[0];
        if (!dailyMetrics[date]) {
          dailyMetrics[date] = {
            date,
            activities: 0,
            logins: 0,
            views: 0,
            bookmarks: 0
          };
        }
        
        dailyMetrics[date].activities += 1;
        
        // Categorize activities
        if (['login', 'app_open'].includes(activity.activity_type)) {
          dailyMetrics[date].logins += 1;
        } else if (['view_artwork', 'artwork_view'].includes(activity.activity_type)) {
          dailyMetrics[date].views += 1;
        } else if (['bookmark', 'bookmark_artwork'].includes(activity.activity_type)) {
          dailyMetrics[date].bookmarks += 1;
        }
      });
    }

    return Object.values(dailyMetrics);

  } catch (error) {
    console.error('Error fetching engagement metrics:', error);
    return [];
  }
};

/**
 * Export user data for admin
 */
export const exportUserData = async (userId, adminUser) => {
  try {
    // Log admin action
    await logAdminAction(adminUser, 'export_user_data', {
      target_user_id: userId
    });

    // Get comprehensive user data
    const [userDetails, activity, stats] = await Promise.all([
      fetchUserDetails(userId),
      getUserActivity(userId, '90d'),
      getUserStats(userId)
    ]);

    const exportData = {
      user: userDetails,
      activity: activity,
      statistics: stats,
      exportedAt: new Date().toISOString(),
      exportedBy: adminUser.uuid
    };

    return exportData;

  } catch (error) {
    console.error('Error exporting user data:', error);
    throw error;
  }
};

/**
 * Get users who actually have the mobile app installed (not just web)
 */
// FIXED VERSION - Replace the existing getUsersWithMobileApp function in adminApi.js

/**
 * Get users who actually have the mobile app installed (not just web)
 * FIXED: Removes problematic .not() filter and handles null values properly
 */
export const getUsersWithMobileApp = async () => {
  try {
    console.log('🔍 Fetching mobile app users...');
    
    // Get ALL device records (don't filter in query - filter in JS instead)
    const { data: allDevices, error: deviceError } = await supabase
      .from('user_player_ids')
      .select('user_id, device_info, player_id, created_at');

    if (deviceError) {
      console.error('❌ Error fetching devices:', deviceError);
      return { mobileUsers: 0, totalDevices: 0, mobileDevices: 0 };
    }

    if (!allDevices || allDevices.length === 0) {
      console.log('⚠️ No device records found in database');
      return { mobileUsers: 0, totalDevices: 0, mobileDevices: 0 };
    }

    console.log(`📊 Found ${allDevices.length} total device records`);

    // Filter for devices that have device_info and are mobile
    const realMobileDevices = allDevices.filter(device => {
      // Skip if no device_info
      if (!device.device_info) return false;

      try {
        let deviceInfo = device.device_info;
        
        // Parse JSON string if needed
        if (typeof deviceInfo === 'string') {
          try {
            deviceInfo = JSON.parse(deviceInfo);
          } catch (parseError) {
            // If JSON parsing fails, treat as string and check for mobile keywords
            const infoLower = deviceInfo.toLowerCase();
            return (
              infoLower.includes('android') ||
              infoLower.includes('ios') ||
              infoLower.includes('iphone') ||
              infoLower.includes('mobile') ||
              infoLower.includes('phone')
            );
          }
        }

        // Check for mobile indicators in parsed object
        if (typeof deviceInfo === 'object' && deviceInfo !== null) {
          const platform = (deviceInfo.platform || '').toLowerCase();
          const deviceType = (deviceInfo.device_type || '').toLowerCase();
          const model = (deviceInfo.model || '').toLowerCase();
          
          // Look for mobile indicators
          const isMobile = (
            // Direct device type matches
            deviceType === 'ios' ||
            deviceType === 'android' ||
            deviceType === 'mobile' ||
            deviceType === 'phone' ||
            deviceType === 'tablet' ||
            
            // Platform matches
            platform.includes('android') ||
            platform.includes('ios') ||
            platform.includes('iphone') ||
            platform.includes('ipad') ||
            
            // Model matches
            model.includes('iphone') ||
            model.includes('android') ||
            
            // Substring matches for device type
            deviceType.includes('mobile') ||
            deviceType.includes('phone') ||
            deviceType.includes('tablet')
          );

          return isMobile;
        }

        return false;
      } catch (error) {
        console.warn('⚠️ Error processing device:', error);
        return false;
      }
    });

    // Get unique user IDs with mobile apps
    const uniqueMobileUserIds = new Set(realMobileDevices.map(d => d.user_id));

    const result = {
      mobileUsers: uniqueMobileUserIds.size,
      totalDevices: allDevices.length,
      mobileDevices: realMobileDevices.length
    };

    console.log('✅ Mobile users analysis complete:', result);
    return result;

  } catch (error) {
    console.error('❌ Error getting mobile app users:', error);
    return { mobileUsers: 0, totalDevices: 0, mobileDevices: 0 };
  }
};



/**
 * Get pulse configuration from site_configurations
 */
export const getPulseConfiguration = async () => {
  try {
    const { data, error } = await supabase
      .from('site_configurations')
      .select('value')
      .eq('key', 'pulse_page_settings')
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      throw error;
    }

    return data?.value || {
      include_insights: true,
      insight_position: 0,
      include_survey: true,
      survey_position: 1,
      include_prompts: true,
      prompt_position: 2
    };
  } catch (error) {
    console.error('Error fetching pulse configuration:', error);
    throw error;
  }
};

/**
 * Update pulse configuration
 */
export const updatePulseConfiguration = async (config, adminUser) => {
  try {
    const { error } = await supabase
      .from('site_configurations')
      .upsert({
        key: 'pulse_page_settings',
        value: config,
        updated_at: new Date().toISOString()
      });

    if (error) throw error;

    // Log admin action
    if (adminUser) {
      await logAdminAction(adminUser, 'update_pulse_config', { config });
    }

    return true;
  } catch (error) {
    console.error('Error updating pulse configuration:', error);
    throw error;
  }
};

/**
 * Get pulse surveys from user_preferences
 */
export const getPulseSurveys = async () => {
  try {
    const { data, error } = await supabase
      .from('user_preferences')
      .select(`
        preference_id,
        user_type,
        question_type,
        question,
        options,
        allow_other,
        featured_on_pulse,
        created_at
      `)
      .eq('featured_on_pulse', true)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching pulse surveys:', error);
    throw error;
  }
};

/**
 * Create new pulse survey
 */
export const createPulseSurvey = async (surveyData, adminUser) => {
  try {
    const { data, error } = await supabase
      .from('user_preferences')
      .insert({
        ...surveyData,
        preference_id: crypto.randomUUID(),
        created_at: new Date().toISOString()
      })
      .select()
      .single();

    if (error) throw error;

    // Log admin action
    if (adminUser) {
      await logAdminAction(adminUser, 'create_enhanced_pulse_survey', { 
        survey_id: data.preference_id,
        question: surveyData.question,
        action_url: surveyData.action_url
      });
    }

    return data;
  } catch (error) {
    console.error('Error creating enhanced pulse survey:', error);
    throw error;
  }
};

/**
 * Update pulse survey
 */
export const updatePulseSurvey = async (surveyId, surveyData, adminUser) => {
  try {
    const { data, error } = await supabase
      .from('user_preferences')
      .update({
        ...surveyData,
        updated_at: new Date().toISOString()
      })
      .eq('preference_id', surveyId)
      .select()
      .single();

    if (error) throw error;

    // Log admin action
    if (adminUser) {
      await logAdminAction(adminUser, 'update_pulse_survey', { 
        survey_id: surveyId,
        changes: surveyData
      });
    }

    return data;
  } catch (error) {
    console.error('Error updating pulse survey:', error);
    throw error;
  }
};

/**
 * Delete pulse survey
 */
export const deletePulseSurvey = async (surveyId, adminUser) => {
  try {
    const { error } = await supabase
      .from('user_preferences')
      .delete()
      .eq('preference_id', surveyId);

    if (error) throw error;

    // Log admin action
    if (adminUser) {
      await logAdminAction(adminUser, 'delete_pulse_survey', { 
        survey_id: surveyId
      });
    }

    return true;
  } catch (error) {
    console.error('Error deleting pulse survey:', error);
    throw error;
  }
};

/**
 * Get pulse analytics data
 */
// Updated getPulseAnalytics function that uses real tracking data
export const getPulseAnalyticsWithRealTracking = async (timeRange = '30d') => {
  try {
    const days = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

    // Get pulse views (the most important metric)
    const { count: pulseViews, error: viewsError } = await supabase
      .from('user_activity')
      .select('*', { count: 'exact', head: true })
      .eq('activity_type', 'pulse_view')
      .gte('timestamp', startDate);

    // Get pulse likes
    const { count: pulseLikes, error: likesError } = await supabase
      .from('user_activity')
      .select('*', { count: 'exact', head: true })
      .eq('activity_type', 'liked_pulse')
      .gte('timestamp', startDate);

    // Get pulse comments
    const { count: pulseComments, error: commentsError } = await supabase
      .from('user_activity')
      .select('*', { count: 'exact', head: true })
      .eq('activity_type', 'pulse_comment')
      .gte('timestamp', startDate);

    // Get smart content interactions
    const { count: smartContentClicks, error: smartError } = await supabase
      .from('user_activity')
      .select('*', { count: 'exact', head: true })
      .in('activity_type', ['survey_response', 'prompt_click', 'insight_click'])
      .gte('timestamp', startDate);

    // Get smart content views (impressions)
    const { count: smartContentViews, error: impressionsError } = await supabase
      .from('user_activity')
      .select('*', { count: 'exact', head: true })
      .in('activity_type', ['survey_view', 'prompt_view', 'insight_view'])
      .gte('timestamp', startDate);

    // Calculate engagement rate
    const engagementRate = pulseViews > 0 ? 
      (((pulseLikes + pulseComments) / pulseViews) * 100) : 0;

    // Calculate smart content conversion rate
    const smartConversionRate = smartContentViews > 0 ? 
      ((smartContentClicks / smartContentViews) * 100) : 0;

    return {
      // Core metrics
      totalPulseViews: pulseViews || 0,
      pulseLikes: pulseLikes || 0,
      pulseComments: pulseComments || 0,
      
      // Smart content metrics
      smartContentShown: smartContentViews || 0,
      smartContentClicks: smartContentClicks || 0,
      
      // Calculated metrics
      engagementRate: parseFloat(engagementRate.toFixed(2)),
      smartConversionRate: parseFloat(smartConversionRate.toFixed(2)),
      
      // Meta
      timeRange: timeRange,
      hasRealData: true
    };

  } catch (error) {
    console.error('Error fetching pulse analytics:', error);
    return {
      totalPulseViews: 0,
      pulseLikes: 0,
      pulseComments: 0,
      smartContentShown: 0,
      smartContentClicks: 0,
      engagementRate: 0,
      smartConversionRate: 0,
      timeRange: timeRange,
      hasRealData: false
    };
  }
};

/**
 * Get survey responses analytics
 */
export const getSurveyResponsesAnalytics = async (surveyId) => {
  try {
    const { data, error } = await supabase
      .from('preference_answers')
      .select('answer_text, created_at')
      .eq('preference_id', surveyId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // Process response data
    const responses = data || [];
    const totalResponses = responses.length;
    
    // Group responses by answer for multiple choice
    const answerCounts = {};
    responses.forEach(response => {
      const answer = typeof response.answer_text === 'string' 
        ? response.answer_text 
        : JSON.stringify(response.answer_text);
      
      answerCounts[answer] = (answerCounts[answer] || 0) + 1;
    });

    return {
      totalResponses,
      responses: responses.slice(0, 50), // Latest 50 responses
      answerCounts,
      responsesByDay: groupResponsesByDay(responses)
    };
  } catch (error) {
    console.error('Error fetching survey responses:', error);
    return {
      totalResponses: 0,
      responses: [],
      answerCounts: {},
      responsesByDay: {}
    };
  }
};

/**
 * Helper function to group responses by day
 */
const groupResponsesByDay = (responses) => {
  const grouped = {};
  responses.forEach(response => {
    const day = new Date(response.created_at).toISOString().split('T')[0];
    grouped[day] = (grouped[day] || 0) + 1;
  });
  return grouped;
};

// ============================================================
// COLLECTION-BASED NOTIFICATION TARGETING
// ============================================================

/**
 * Get collections for notification targeting dropdown
 */
export const getCollectionsForTargeting = async () => {
  try {
    const { data, error } = await supabase
      .from('collections')
      .select('collection_id, title, type, created_at')
      .eq('access_control', 'Public')
      .order('created_at', { ascending: false })
      .limit(100);
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching collections for targeting:', error);
    return [];
  }
};

/**
 * Get artists from a specific collection with their app/email status
 * This joins artist_collection_mapping and collection_vc_mapping to find all artists
 */
export const getArtistsInCollection = async (collectionId, options = {}) => {
  const { hasAppOnly = false, hasEmailOnly = false } = options;
  
  try {
    // Get artists from artist_collection_mapping (directly mapped artists)
    const { data: directArtists, error: directError } = await supabase
      .from('artist_collection_mapping')
      .select(`
        user_uuid,
        users!artist_collection_mapping_user_uuid_fkey (
          uuid,
          email,
          first_name,
          last_name,
          nickname,
          preferences
        )
      `)
      .eq('collection_id', collectionId);
    
    if (directError) {
      console.error('Error fetching direct artists:', directError);
    }

    // Get artists from collection_vc_mapping (artists with artwork in collection)
    const { data: artworkArtists, error: artworkError } = await supabase
      .from('collection_vc_mapping')
      .select(`
        vcs!collection_vc_mapping_vc_id_fkey (
          uuid,
          users!vcs_uuid_fkey (
            uuid,
            email,
            first_name,
            last_name,
            nickname,
            preferences
          )
        )
      `)
      .eq('collection_id', collectionId);
    
    if (artworkError) {
      console.error('Error fetching artwork artists:', artworkError);
    }

    // Combine and deduplicate artists
    const artistMap = new Map();
    
    // Add direct artists
    (directArtists || []).forEach(mapping => {
      if (mapping.users) {
        artistMap.set(mapping.users.uuid, mapping.users);
      }
    });
    
    // Add artists from artworks
    (artworkArtists || []).forEach(mapping => {
      if (mapping.vcs?.users) {
        artistMap.set(mapping.vcs.users.uuid, mapping.vcs.users);
      }
    });

    let artists = Array.from(artistMap.values());
    console.log(`Found ${artists.length} unique artists in collection ${collectionId}`);

    // Get app users if filtering
    if (hasAppOnly || !hasEmailOnly) {
      const { data: appUsers } = await supabase
        .from('user_player_ids')
        .select('user_id');
      
      const appUserIds = new Set(appUsers?.map(u => u.user_id) || []);
      
      // Add hasApp flag to each artist
      artists = artists.map(artist => ({
        ...artist,
        hasApp: appUserIds.has(artist.uuid)
      }));
      
      if (hasAppOnly) {
        artists = artists.filter(a => a.hasApp);
        console.log(`Filtered to ${artists.length} artists with mobile app`);
      }
    }

    // Filter by email if needed
    if (hasEmailOnly) {
      artists = artists.filter(a => a.email && a.email.includes('@'));
      console.log(`Filtered to ${artists.length} artists with email`);
    }

    return artists;
  } catch (error) {
    console.error('Error getting artists in collection:', error);
    return [];
  }
};

/**
 * Get email templates for admin notification sending
 */
export const getEmailTemplates = async () => {
  try {
    const { data, error } = await supabase
      .from('organization_email_templates')
      .select('id, template_key, subject, audience_type, mandrill_template_name, html_body')
      .eq('is_active', true)
      .order('template_key');
    
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching email templates:', error);
    return [];
  }
};

/**
 * Send email to users via Mandrill
 */
export const sendEmailToUsers = async (recipients, emailData) => {
  const { subject, templateName, htmlBody, mergeVars = [] } = emailData;
  
  const results = [];
  
  for (const recipient of recipients) {
    try {
      const { data, error } = await supabase.functions.invoke('send-mandrill-email', {
        body: {
          template_name: templateName || 'admin-notification',
          subject: subject,
          message: {
            to: [{ email: recipient.email, name: recipient.nickname || recipient.first_name || 'Artist' }],
            global_merge_vars: [
              { name: 'FIRST_NAME', content: recipient.first_name || recipient.nickname || 'Artist' },
              { name: 'SUBJECT', content: subject },
              { name: 'BODY', content: htmlBody || '' },
              ...mergeVars
            ]
          }
        }
      });
      
      if (error) {
        results.push({ userId: recipient.uuid, email: recipient.email, success: false, error: error.message });
      } else {
        results.push({ userId: recipient.uuid, email: recipient.email, success: true });
      }
    } catch (err) {
      results.push({ userId: recipient.uuid, email: recipient.email, success: false, error: err.message });
    }
  }
  
  return results;
};

// ============================================================
// Unified Notification Dashboard Queries
// ============================================================

/**
 * Get unified notification stats across email and push channels.
 * Used by Overview tab KPI cards.
 * @param {string} timeRange - 'day' | 'week' | 'month' | 'quarter'
 */
export const getUnifiedNotificationStats = async (timeRange = 'week') => {
  const rangeMs = {
    day: 1 * 24 * 60 * 60 * 1000,
    week: 7 * 24 * 60 * 60 * 1000,
    month: 30 * 24 * 60 * 60 * 1000,
    quarter: 90 * 24 * 60 * 60 * 1000,
  };
  const ms = rangeMs[timeRange] || rangeMs.week;
  const since = new Date(Date.now() - ms).toISOString();
  const priorStart = new Date(Date.now() - ms * 2).toISOString();

  try {
    const [emailCurrent, emailPrior, pushCurrent, pushPrior] = await Promise.all([
      fetchAllRows(() => supabase.from('email_logs').select('id, status, open_count, click_count').gte('sent_at', since)),
      fetchAllRows(() => supabase.from('email_logs').select('id, status, open_count, click_count').gte('sent_at', priorStart).lt('sent_at', since)),
      fetchAllRows(() => supabase.from('push_delivery_logs').select('id, status').gte('created_at', since)),
      fetchAllRows(() => supabase.from('push_delivery_logs').select('id, status').gte('created_at', priorStart).lt('created_at', since)),
    ]);

    const emails = emailCurrent || [];
    const emailsPrior = emailPrior || [];
    const pushes = pushCurrent || [];
    const pushesPrior = pushPrior || [];

    const totalSent = emails.length + pushes.length;
    const totalSentPrior = emailsPrior.length + pushesPrior.length;

    const delivered = emails.filter(e => ['delivered', 'opened', 'clicked'].includes(e.status)).length
      + pushes.filter(p => ['delivered', 'opened'].includes(p.status)).length;
    const deliveredPrior = emailsPrior.filter(e => ['delivered', 'opened', 'clicked'].includes(e.status)).length
      + pushesPrior.filter(p => ['delivered', 'opened'].includes(p.status)).length;

    const opened = emails.filter(e => ['opened', 'clicked'].includes(e.status)).length
      + pushes.filter(p => p.status === 'opened').length;
    const openedPrior = emailsPrior.filter(e => ['opened', 'clicked'].includes(e.status)).length
      + pushesPrior.filter(p => p.status === 'opened').length;

    const clicked = emails.filter(e => e.status === 'clicked').length;
    const clickedPrior = emailsPrior.filter(e => e.status === 'clicked').length;

    const bounced = emails.filter(e => e.status === 'bounced').length
      + pushes.filter(p => p.status === 'failed').length;
    const bouncedPrior = emailsPrior.filter(e => e.status === 'bounced').length
      + pushesPrior.filter(p => p.status === 'failed').length;

    const pct = (num, den) => den > 0 ? Math.round((num / den) * 1000) / 10 : 0;
    const trend = (current, prior) => {
      if (prior === 0) return current > 0 ? 100 : 0;
      return Math.round(((current - prior) / prior) * 1000) / 10;
    };

    return {
      sent: { value: totalSent, trend: trend(totalSent, totalSentPrior) },
      deliveryRate: { value: pct(delivered, totalSent), trend: trend(pct(delivered, totalSent), pct(deliveredPrior, totalSentPrior)) },
      openRate: { value: pct(opened, delivered), trend: trend(pct(opened, delivered), pct(openedPrior, deliveredPrior)) },
      clickRate: { value: pct(clicked, delivered), trend: trend(pct(clicked, delivered), pct(clickedPrior, deliveredPrior)) },
      bounceRate: { value: pct(bounced, totalSent), trend: trend(pct(bounced, totalSent), pct(bouncedPrior, totalSentPrior)) },
      funnel: { sent: totalSent, delivered, opened, clicked, bounced },
    };
  } catch (error) {
    console.error('Error getting unified notification stats:', error);
    return {
      sent: { value: 0, trend: 0 },
      deliveryRate: { value: 0, trend: 0 },
      openRate: { value: 0, trend: 0 },
      clickRate: { value: 0, trend: 0 },
      bounceRate: { value: 0, trend: 0 },
      funnel: { sent: 0, delivered: 0, opened: 0, clicked: 0, bounced: 0 },
    };
  }
};

/**
 * Get activity feed of recent notifications across both channels.
 * @param {object} filters - { channel, status, type, search, timeRange }
 * @param {object} pagination - { limit, offset }
 */
export const getNotificationActivityFeed = async (filters = {}, pagination = { limit: 50, offset: 0 }) => {
  const { channel, status, type, search, timeRange = 'week' } = filters;
  const { limit, offset } = pagination;

  const rangeMs = { day: 1, week: 7, month: 30, quarter: 90 };
  const days = rangeMs[timeRange] || 7;
  const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

  try {
    const items = [];

    if (!channel || channel === 'email') {
      let emailQuery = supabase
        .from('email_logs')
        .select('id, user_email, user_uuid, subject, template_name, template_key, status, open_count, click_count, sent_at, delivered_at, opened_at, clicked_at, bounced_at, organization_id')
        .gte('sent_at', since)
        .order('sent_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (status && status !== 'all') {
        emailQuery = emailQuery.eq('status', status);
      }
      if (search) {
        emailQuery = emailQuery.ilike('user_email', `%${search}%`);
      }

      const { data: emailData } = await emailQuery;

      for (const email of (emailData || [])) {
        items.push({
          id: `email-${email.id}`,
          channel: 'email',
          title: email.subject || email.template_name || 'Email',
          subtitle: email.template_key || email.template_name || '',
          recipient: email.user_email,
          recipientId: email.user_uuid,
          type: email.template_name === 'organization_email_hub' ? 'campaign' : 'auto',
          status: email.status || 'sent',
          openCount: email.open_count || 0,
          clickCount: email.click_count || 0,
          createdAt: email.sent_at,
          deliveredAt: email.delivered_at,
          openedAt: email.opened_at,
          clickedAt: email.clicked_at,
          bouncedAt: email.bounced_at,
        });
      }
    }

    if (!channel || channel === 'push') {
      let pushQuery = supabase
        .from('notifications')
        .select(`
          id, user_id, title, body, type, category, priority, notification_sent, read, created_at, metadata,
          push_delivery_logs (id, status, delivered_at, opened_at, device_type)
        `)
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      const { data: pushData } = await pushQuery;

      for (const notif of (pushData || [])) {
        const deliveryLog = notif.push_delivery_logs?.[0];
        const pushStatus = deliveryLog?.status || (notif.notification_sent ? 'sent' : 'pending');

        if (status && status !== 'all' && pushStatus !== status) continue;

        items.push({
          id: `push-${notif.id}`,
          channel: 'push',
          title: notif.title || 'Push Notification',
          subtitle: notif.body || '',
          recipient: notif.metadata?.sender_info?.email || notif.user_id,
          recipientId: notif.user_id,
          type: notif.metadata?.admin_broadcast ? 'campaign' : (notif.category === 'collector_inquiry' ? 'inquiry' : 'auto'),
          status: pushStatus,
          openCount: pushStatus === 'opened' ? 1 : 0,
          clickCount: 0,
          createdAt: notif.created_at,
          deliveredAt: deliveryLog?.delivered_at,
          openedAt: deliveryLog?.opened_at,
          deviceType: deliveryLog?.device_type,
        });
      }
    }

    let filtered = items;
    if (type && type !== 'all') {
      filtered = items.filter(item => item.type === type);
    }

    filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    return {
      items: filtered.slice(0, limit),
      total: filtered.length,
    };
  } catch (error) {
    console.error('Error getting notification activity feed:', error);
    return { items: [], total: 0 };
  }
};

/**
 * Get time-series analytics data for charts.
 * @param {string} timeRange - 'week' | 'month' | 'quarter'
 * @param {string} channel - 'email' | 'push' | null (all)
 */
export const getUnifiedNotificationAnalytics = async (timeRange = 'month', channel = null) => {
  const rangeMs = { week: 7, month: 30, quarter: 90 };
  const days = rangeMs[timeRange] || 30;
  const since = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

  try {
    const dailyData = {};

    for (let i = 0; i < days; i++) {
      const date = new Date(Date.now() - (days - 1 - i) * 24 * 60 * 60 * 1000);
      const key = date.toISOString().split('T')[0];
      dailyData[key] = { date: key, emailSent: 0, pushSent: 0, emailDelivered: 0, pushDelivered: 0, emailOpened: 0, pushOpened: 0, emailClicked: 0, emailBounced: 0, pushFailed: 0 };
    }

    if (!channel || channel === 'email') {
      const emails = await fetchAllRows(() =>
        supabase.from('email_logs').select('status, sent_at, open_count, click_count').gte('sent_at', since)
      );

      for (const email of emails) {
        const key = new Date(email.sent_at).toISOString().split('T')[0];
        if (dailyData[key]) {
          dailyData[key].emailSent++;
          if (['delivered', 'opened', 'clicked'].includes(email.status)) dailyData[key].emailDelivered++;
          if (['opened', 'clicked'].includes(email.status)) dailyData[key].emailOpened++;
          if (email.status === 'clicked') dailyData[key].emailClicked++;
          if (email.status === 'bounced') dailyData[key].emailBounced++;
        }
      }
    }

    if (!channel || channel === 'push') {
      const pushLogs = await fetchAllRows(() =>
        supabase.from('push_delivery_logs').select('status, created_at').gte('created_at', since)
      );

      for (const push of pushLogs) {
        const key = new Date(push.created_at).toISOString().split('T')[0];
        if (dailyData[key]) {
          dailyData[key].pushSent++;
          if (['delivered', 'opened'].includes(push.status)) dailyData[key].pushDelivered++;
          if (push.status === 'opened') dailyData[key].pushOpened++;
          if (push.status === 'failed') dailyData[key].pushFailed++;
        }
      }
    }

    return Object.values(dailyData);
  } catch (error) {
    console.error('Error getting notification analytics:', error);
    return [];
  }
};

/**
 * Get health metrics for each workflow in the registry.
 * @returns {object} - Raw data for the caller to compute per-workflow metrics
 */
export const getWorkflowHealth = async () => {
  const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

  try {
    const [recentEmails, recentPush] = await Promise.all([
      fetchAllRows(() => supabase.from('email_logs').select('id, template_name, template_key, status, sent_at').gte('sent_at', sevenDaysAgo)),
      fetchAllRows(() => supabase.from('notifications').select('id, category, created_at, push_delivery_logs(status)').gte('created_at', sevenDaysAgo)),
    ]);

    const { data: latestEmails } = await supabase
      .from('email_logs')
      .select('template_name, template_key, sent_at')
      .order('sent_at', { ascending: false })
      .limit(200);

    const { data: latestPush } = await supabase
      .from('notifications')
      .select('category, created_at')
      .order('created_at', { ascending: false })
      .limit(200);

    return {
      emails: recentEmails,
      latestEmails: latestEmails || [],
      pushes: recentPush,
      latestPush: latestPush || [],
    };
  } catch (error) {
    console.error('Error getting workflow health:', error);
    return { emails: [], latestEmails: [], pushes: [], latestPush: [] };
  }
};

/**
 * Get detailed delivery data for a single notification.
 * @param {number|string} id - The email_logs.id or notifications.id
 * @param {'email'|'push'} channel
 */
export const getNotificationDetail = async (id, channel) => {
  try {
    if (channel === 'email') {
      const { data, error } = await supabase
        .from('email_logs')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    } else {
      const { data, error } = await supabase
        .from('notifications')
        .select(`
          *,
          push_delivery_logs (*)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data;
    }
  } catch (error) {
    console.error('Error getting notification detail:', error);
    return null;
  }
};